export type FeatureFlagName =
  | 'efficacyIntelligenceTier2Preview'
  | 'liveOutcomes'
  | 'liveSignals'
  | 'copilotChat';

export type LiveFeatureFlagName = 'liveOutcomes' | 'liveSignals' | 'copilotChat';

export type FeatureFlagSurfaceState = 'disabled' | 'loading' | 'live' | 'fallback' | 'error';

export type FeatureFlagStateReason =
  | 'frontend-disabled'
  | 'api-disabled'
  | 'loading'
  | 'live'
  | 'fallback'
  | 'error';

export interface FeatureFlagStateCopy {
  label: string;
  title: string;
  body: string;
}

export interface LiveFeatureFlagConfig {
  flag: LiveFeatureFlagName;
  envVar: string;
  apiFeature: 'outcomes' | 'signals' | 'copilot_chat';
  copy: Record<FeatureFlagSurfaceState, FeatureFlagStateCopy>;
}

export interface ResolveFeatureFlagStateOptions {
  loading?: boolean;
  error?: unknown;
  live?: boolean;
  fallback?: boolean;
  apiDisabled?: boolean;
}

export interface ResolvedFeatureFlagState {
  flag: LiveFeatureFlagName;
  enabled: boolean;
  state: FeatureFlagSurfaceState;
  reason: FeatureFlagStateReason;
  envVar: string;
  apiFeature: LiveFeatureFlagConfig['apiFeature'];
  copy: FeatureFlagStateCopy;
}

const truthyValues = new Set(['1', 'true', 'yes', 'on']);

function readEnvFlag(key: string, defaultValue: boolean): boolean {
  const value = (import.meta.env as Record<string, string | undefined>)[key];
  if (value == null) return defaultValue;
  return truthyValues.has(value.toLowerCase());
}

export const featureFlags: Record<FeatureFlagName, boolean> = {
  efficacyIntelligenceTier2Preview: readEnvFlag('VITE_EFFICACY_INTELLIGENCE_TIER2_PREVIEW', true),
  liveOutcomes: readEnvFlag('VITE_LINEAGE_ENABLE_OUTCOMES', false),
  liveSignals: readEnvFlag('VITE_LINEAGE_ENABLE_SIGNALS', false),
  copilotChat: readEnvFlag('VITE_LINEAGE_ENABLE_COPILOT_CHAT', false),
};

export const liveFeatureFlagConfigs: Record<LiveFeatureFlagName, LiveFeatureFlagConfig> = {
  liveOutcomes: {
    flag: 'liveOutcomes',
    envVar: 'VITE_LINEAGE_ENABLE_OUTCOMES',
    apiFeature: 'outcomes',
    copy: {
      disabled: {
        label: 'Outcome endpoints off',
        title: 'Outcome capture disabled',
        body: 'Outcome instruments, measurements, and writes stay disabled until the frontend flag and backend gate are both on.',
      },
      loading: {
        label: 'Loading outcomes',
        title: 'Checking outcome endpoints',
        body: 'Loading outcome instruments and recorded measurements before enabling capture.',
      },
      live: {
        label: 'Outcome endpoints live',
        title: 'Outcome capture connected',
        body: 'Outcome instruments and measurements are coming from the authenticated API.',
      },
      fallback: {
        label: 'Outcome preview mode',
        title: 'Outcome data unavailable',
        body: 'Live outcome data is not available. Show preview or empty states and keep writes guarded.',
      },
      error: {
        label: 'Outcome endpoint issue',
        title: 'Outcome data could not load',
        body: 'The outcome API returned an error. Keep writes guarded and show a retry path.',
      },
    },
  },
  liveSignals: {
    flag: 'liveSignals',
    envVar: 'VITE_LINEAGE_ENABLE_SIGNALS',
    apiFeature: 'signals',
    copy: {
      disabled: {
        label: 'Signal review off',
        title: 'Signals disabled',
        body: 'Signal generation and review endpoints are disabled until the frontend flag and backend gate are both on.',
      },
      loading: {
        label: 'Loading signals',
        title: 'Loading signals to investigate',
        body: 'Loading candidate signals for human review. These are not care recommendations.',
      },
      live: {
        label: 'Signal review live',
        title: 'Signals to investigate connected',
        body: 'Candidate signals are coming from the authenticated API and require human review.',
      },
      fallback: {
        label: 'Preview signals',
        title: 'Using signal preview states',
        body: 'Use example or empty signal states only. Do not present them as claims.',
      },
      error: {
        label: 'Signal endpoint issue',
        title: 'Signals could not load',
        body: 'The signals API returned an error. Show review-safe copy and avoid claim language.',
      },
    },
  },
  copilotChat: {
    flag: 'copilotChat',
    envVar: 'VITE_LINEAGE_ENABLE_COPILOT_CHAT',
    apiFeature: 'copilot_chat',
    copy: {
      disabled: {
        label: 'Co-pilot chat off',
        title: 'Co-pilot chat disabled',
        body: 'Chat endpoints stay disabled until the frontend flag and backend gate are both on.',
      },
      loading: {
        label: 'Loading chat',
        title: 'Loading conversations',
        body: 'Loading co-pilot conversations from the authenticated API.',
      },
      live: {
        label: 'Co-pilot chat live',
        title: 'Co-pilot chat connected',
        body: 'Conversations and messages are using the approved authenticated API routes.',
      },
      fallback: {
        label: 'Co-pilot preview mode',
        title: 'Co-pilot chat unavailable',
        body: 'Keep the composer disabled and show non-interactive prompt examples only.',
      },
      error: {
        label: 'Co-pilot endpoint issue',
        title: 'Co-pilot chat could not load',
        body: 'Chat routes returned an error. Keep message send disabled and surface a retry path.',
      },
    },
  },
};

export function isFeatureEnabled(flag: FeatureFlagName): boolean {
  return featureFlags[flag];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object';
}

function readString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function readFeatureDisabledErrorFeature(error: unknown): string | null | undefined {
  if (!isRecord(error)) return undefined;

  const directFeatureDisabled = error.feature_disabled === true || error.featureDisabled === true;
  const directFeature = readString(error.feature);
  const body = isRecord(error.body) ? error.body : null;
  const bodyFeatureDisabled = body?.feature_disabled === true || body?.featureDisabled === true;
  const bodyFeature = readString(body?.feature);

  if (!directFeatureDisabled && !bodyFeatureDisabled) return undefined;
  return bodyFeature ?? directFeature;
}

export function isFeatureDisabledForFlag(flag: LiveFeatureFlagName, error: unknown): boolean {
  const disabledFeature = readFeatureDisabledErrorFeature(error);
  if (disabledFeature === undefined) return false;
  return disabledFeature == null || disabledFeature === liveFeatureFlagConfigs[flag].apiFeature;
}

export function getFeatureFlagStateCopy(
  flag: LiveFeatureFlagName,
  state: FeatureFlagSurfaceState,
): FeatureFlagStateCopy {
  return liveFeatureFlagConfigs[flag].copy[state];
}

export function resolveFeatureFlagState(
  flag: LiveFeatureFlagName,
  options: ResolveFeatureFlagStateOptions = {},
): ResolvedFeatureFlagState {
  const config = liveFeatureFlagConfigs[flag];
  const enabled = isFeatureEnabled(flag);
  const apiDisabled = options.apiDisabled === true || isFeatureDisabledForFlag(flag, options.error);

  let state: FeatureFlagSurfaceState;
  let reason: FeatureFlagStateReason;

  if (!enabled) {
    state = 'disabled';
    reason = 'frontend-disabled';
  } else if (apiDisabled) {
    state = 'disabled';
    reason = 'api-disabled';
  } else if (options.loading) {
    state = 'loading';
    reason = 'loading';
  } else if (options.error) {
    state = 'error';
    reason = 'error';
  } else if (options.live) {
    state = 'live';
    reason = 'live';
  } else if (options.fallback === false) {
    state = 'live';
    reason = 'live';
  } else {
    state = 'fallback';
    reason = 'fallback';
  }

  return {
    flag,
    enabled,
    state,
    reason,
    envVar: config.envVar,
    apiFeature: config.apiFeature,
    copy: config.copy[state],
  };
}
